<template>
  <el-form
    ref="formRef"
    class="flex"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div class="oa-form-left">
      <el-form-item prop="contactName" :label="`${prefix}姓名`">
        <el-input
          v-model="formValues.contactName"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>

      <el-form-item prop="contactEmail" :label="`${prefix}邮箱`">
        <el-input
          v-model="formValues.contactEmail"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
    </div>

    <div class="oa-form-right">
      <el-form-item prop="contactPhone" :label="`${prefix}手机号`">
        <el-input
          v-model="formValues.contactPhone"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { FormInstance, FormRules } from 'element-plus';
import { PropType, reactive, ref, toRaw, watch } from 'vue';
import { IContactPerson } from './type';
import { checkEmail, checkPhone, scrollToErrorField } from '@core/tools';

const props = defineProps({
  data: {
    type: Object as PropType<IContactPerson>,
    default: () => {
      return {} as IContactPerson;
    },
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  prefix: {
    type: String,
    default: '联系人',
  },
});

const emit = defineEmits<{
  'key-enter': [];
  'scroll-to': [value: string];
}>();

const formRef = ref<FormInstance>();

const formValues = ref({} as IContactPerson);

const rules = reactive<FormRules<IContactPerson>>({
  contactName: [
    {
      required: true,
      message: '请输入联系人姓名',
      trigger: 'blur',
    },
  ],
  contactPhone: [
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkPhone(formValues.value.contactPhone)) {
          callback(checkPhone(formValues.value.contactPhone, '联系人'));
        }
        callback();
      },
    },
  ],
  contactEmail: [
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkEmail(formValues.value.contactEmail)) {
          callback(checkEmail(formValues.value.contactEmail));
        }
        callback();
      },
    },
  ],
});

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as IContactPerson;

  formRef.value?.resetFields?.();
  formValues.value = {
    contactName: d.contactName,
    contactPhone: d.contactPhone,
    contactEmail: d.contactEmail,
  };
}

watch(
  () => props.data,
  () => {
    resetFormValues();
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => toRaw(formValues.value),
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});
</script>

<style scoped lang="scss" src="../index.scss" />

